/**
 * 🌐 CLIENTE API CENTRALIZADO - LegalPlus
 * 
 * Cliente HTTP configurado con:
 * - Interceptores de seguridad
 * - Manejo centralizado de errores
 * - Rate limiting
 * - Logging seguro
 */

import axios from 'axios';
import { clearSession } from './securityUtils';

// Configuración inteligente de la API usando variables de entorno de CRA
export const API_BASE_URL = process.env.REACT_APP_API_URL || '/legalplus';

/**
 * Instancia de axios configurada
 */
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000, // 30 segundos
  withCredentials: true, // Importante para cookies HttpOnly
  headers: {
    'Content-Type': 'application/json',
    'X-Requested-With': 'XMLHttpRequest', // Protección CSRF básica
  }
});

/**
 * Interceptor de Request - Seguridad
 */
apiClient.interceptors.request.use(
  (config) => {
    // Agregar timestamp para prevenir cache poisoning
    config.headers['X-Request-Time'] = Date.now();

    // Log seguro (sin datos sensibles)
    if (process.env.NODE_ENV === 'development') {
      console.log(`📤 API Request: ${config.method?.toUpperCase()} ${config.url}`);
    }

    return config;
  },
  (error) => {
    console.error('❌ Error en request:', error);
    return Promise.reject(error);
  }
);

/**
 * Interceptor de Response - Manejo de errores y seguridad
 */
apiClient.interceptors.response.use(
  (response) => {
    // Log seguro de respuestas exitosas
    if (process.env.NODE_ENV === 'development') {
      console.log(`✅ API Response: ${response.config.url} - Status: ${response.status}`);
    }

    return response;
  },
  (error) => {
    const { response, config } = error;

    // Log de error sin exponer datos sensibles
    if (process.env.NODE_ENV === 'development') {
      console.error(`❌ API Error: ${config?.url} - Status: ${response?.status}`);
    }

    // Manejo específico de códigos de error
    if (response) {
      switch (response.status) {
        case 401:
          // Sesión expirada o no autorizado
          console.warn('⚠️ Sesión expirada o no autorizada');
          clearSession();

          // Redirigir a login de forma absoluta (App vive en /)
          if (!window.location.pathname.includes('/login')) {
            window.location.href = '/login';
          }
          break;

        case 403:
          // Acceso prohibido
          console.warn('⚠️ Acceso prohibido');
          break;

        case 404:
          // Recurso no encontrado
          console.warn('⚠️ Recurso no encontrado');
          break;

        case 429:
          // Demasiadas peticiones (rate limiting)
          console.warn('⚠️ Demasiadas peticiones, intenta más tarde');
          break;

        case 500:
        case 502:
        case 503:
          // Errores del servidor
          console.error('❌ Error del servidor');
          break;

        default:
          console.error(`❌ Error HTTP ${response.status}`);
      }
    } else if (error.request) {
      // Request hecho pero sin respuesta
      console.error('❌ Sin respuesta del servidor');
    } else {
      // Error al configurar el request
      console.error('❌ Error en la configuración de la petición');
    }

    // Mensaje de error: si el backend envió un error estructurado (campo "error" o "message"),
    // priorizarlo para que el usuario vea la causa real (validación, duplicado, columna inexistente, etc.).
    // El backend ya controla qué exponer, así que mostrarlo es seguro y mucho más útil para depurar.
    const backendMsg = response?.data?.error || response?.data?.message;
    const statusesQueMuestranBackend = new Set([400, 404, 409, 422, 500]);
    const usarBackend = backendMsg && statusesQueMuestranBackend.has(response?.status);
    const safeError = {
      status: response?.status,
      message: usarBackend ? backendMsg : getSafeErrorMessage(response?.status),
      backendMessage: backendMsg || null,
      code: response?.data?.code
    };

    return Promise.reject(safeError);
  }
);

/**
 * Obtiene mensajes de error seguros para mostrar al usuario
 * Evita exponer detalles técnicos
 * @param {number} status - Código de estado HTTP
 * @returns {string}
 */
const getSafeErrorMessage = (status) => {
  const messages = {
    400: 'Los datos enviados no son válidos',
    401: 'Tu sesión ha expirado. Por favor, inicia sesión nuevamente',
    403: 'No tienes permisos para realizar esta acción',
    404: 'El recurso solicitado no fue encontrado',
    429: 'Demasiadas solicitudes. Intenta nuevamente en unos momentos',
    500: 'Error en el servidor. Intenta más tarde',
    502: 'Servicio temporalmente no disponible',
    503: 'Servicio en mantenimiento. Intenta más tarde'
  };

  return messages[status] || 'Ocurrió un error. Por favor, intenta nuevamente';
};

/**
 * Métodos del cliente API
 */
export const api = {
  /**
   * GET request
   */
  get: async (url, config = {}) => {
    try {
      const response = await apiClient.get(url, config);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * POST request
   */
  post: async (url, data = {}, config = {}) => {
    try {
      const response = await apiClient.post(url, data, config);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * PUT request
   */
  put: async (url, data = {}, config = {}) => {
    try {
      const response = await apiClient.put(url, data, config);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * PATCH request
   */
  patch: async (url, data = {}, config = {}) => {
    try {
      const response = await apiClient.patch(url, data, config);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * DELETE request
   */
  delete: async (url, config = {}) => {
    try {
      const response = await apiClient.delete(url, config);
      return response.data;
    } catch (error) {
      throw error;
    }
  }
};

/**
 * 📋 INTEGRACIÓN CON N8N - Encuestas de Satisfacción
 * 
 * Envía datos de encuesta al flujo de n8n
 * @param {Object} encuestaData - Datos de la encuesta
 * @returns {Promise}
 */
export const enviarEncuestaSatisfaccion = async (encuestaData) => {
  try {
    // URL del webhook de automatizacion. Se configura por entorno:
    // REACT_APP_N8N_WEBHOOK_URL en el .env del frontend.
    const N8N_WEBHOOK_URL = process.env.REACT_APP_N8N_WEBHOOK_URL;
    if (!N8N_WEBHOOK_URL) {
      throw new Error(
        'REACT_APP_N8N_WEBHOOK_URL no esta configurada. Defina la URL del webhook ' +
        'de encuestas en el archivo .env del frontend antes de compilar.'
      );
    }

    // Identificador opcional del destinatario de la notificacion (chat de mensajeria).
    const NOTIFY_CHAT_ID = process.env.REACT_APP_NOTIFY_CHAT_ID || '';

    // Preparar datos para enviar al webhook
    const payload = {
      name: encuestaData.cliente,
      cliente: encuestaData.cliente,
      email: encuestaData.email,
      caso: encuestaData.caso || 'Sin especificar',
      calificacion: encuestaData.calificacion,
      comentarios: encuestaData.comentarios || '',
      fecha: new Date().toISOString(),
      tipo: 'encuesta_satisfaccion',
      workflow: 'Schedule Encuesta',
      chatId: NOTIFY_CHAT_ID
    };

    console.log('📤 Enviando encuesta a n8n (PRODUCCIÓN)');
    console.log('🔗 URL:', N8N_WEBHOOK_URL);
    console.log('📦 Payload:', JSON.stringify(payload, null, 2));

    // Usar fetch en lugar de axios para mejor manejo de CORS
    const response = await fetch(N8N_WEBHOOK_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
      mode: 'cors', // Permite CORS
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();

    console.log('✅ Encuesta enviada exitosamente a n8n');
    console.log('📨 Respuesta:', data);
    console.log('📊 Status:', response.status);

    return data;
  } catch (error) {
    console.error('❌ Error al enviar encuesta a n8n:', error);

    // Manejo específico de errores de fetch
    if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
      console.error('❌ Error de CORS o de red - el servidor n8n puede no estar respondiendo');
      throw new Error('No se pudo conectar con n8n. Verifica que el webhook esté activo y configurado para permitir CORS.');
    }

    throw new Error(error.message || 'Error al comunicarse con n8n');
  }
};

/**
 * Exportaciones
 */
export default apiClient;
